import React, { useEffect,useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { AppWrapper, Spinner } from 'components';
import AffiliateBusinessSelector from 'components/AffiliateBusinessSelector/AffiliateBusinessSelector';
import AffiliateCouponsList from 'components/AffiliateCouponsList/AffiliateCouponsList';
import AffiliateCustomCoupon from 'components/AffiliateCustomCoupon/AffiliateCustomCoupon';
import Breadcrumbs from 'components/BreadCrumbs/BreadCrumbs';
import Pagination from 'components/Pagination/Pagination';
import ProductsStatus from 'components/ProductsStatus/ProductsStatus';
import { AFFILIATE_TYPES,COUPON_EXPIRY_TYPES, COUPON_STATES, COUPON_TYPES } from 'coupon-common';
import { actions as affiliateActions } from 'coupon-common/src/modules/affiliateBusiness';
import { actions as rakutenActions } from 'coupon-common/src/modules/rakuten';
import { calculateSavings, parseISOString } from 'coupon-common/src/utils';

// ─── Debug logger ────────────────────────────────────────────────────────────
const DEBUG = false; // flip to false to silence all logs

const log = (label, data) => {
  if (!DEBUG) return;
  const ts = new Date().toISOString();
  if (data !== undefined) {
    console.log(`[RakutenCouponList][${ts}] ${label}`, data);
  } else {
    console.log(`[RakutenCouponList][${ts}] ${label}`);
  }
};

const logWarn = (label, data) => {
  if (!DEBUG) return;
  const ts = new Date().toISOString();
  console.warn(`[RakutenCouponList][${ts}] ⚠️  ${label}`, data ?? '');
};

const logError = (label, data) => {
  const ts = new Date().toISOString();
  console.error(`[RakutenCouponList][${ts}] ❌ ${label}`, data ?? '');
};
// ─────────────────────────────────────────────────────────────────────────────

const RakutenCouponList = () => {
  const dispatch = useDispatch();

  const AFFILIATE_TYPE = AFFILIATE_TYPES.rakuten;

  // ── Selectors ──────────────────────────────────────────────────────────────
  const rakutenApprovedMids = useSelector((state) => {
    const val = state.rakuten.businesses || [];
    log('selector:rakuten.businesses', val);
    return val;
  });

const affiliateBusinesses = useSelector((state) => {
  const val = state.affiliateBusiness?.data?.results || [];
  log('selector:affiliateBusiness.data', val);
  return val;
});

  const products = useSelector((state) => {
    const val = state.rakuten.products || [];
    log('selector:rakuten.products', val);
    return val;
  });

  const rakutenLoading = useSelector((state) => {
    const val = state.rakuten.loading;
    log('selector:rakuten.loading', val);
    return val;
  });

  // ── Local state ────────────────────────────────────────────────────────────
  const [inputValue, setInputValue] = useState('');
  const [productsRequested, setProductsRequested] = useState(false);
  const [discountedProducts, setDiscountedProducts] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState();
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 10;
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [firstProductIndex, setFirstProductIndex] = useState(1);
  const [lastProductIndex, setLastProductIndex] = useState(10);
  const [showCustomCoupon, setShowCustomCoupon] = useState(false);

  // ── Mount: dispatch initial fetches ───────────────────────────────────────
  useEffect(() => {
    log('mount — dispatching getBusinesses() and getAffiliateBusinesses()');
    log('environment check', {
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_API_URL: process.env.REACT_APP_API_URL,       // adjust key to match your .env
      REACT_APP_BASE_URL: process.env.REACT_APP_BASE_URL,     // adjust key to match your .env
      origin: window.location.origin,
      href: window.location.href,
    });

    const t0 = performance.now();

    const businessesPromise = dispatch(rakutenActions.getBusinesses());
    log('getBusinesses() dispatched — promise', businessesPromise);

    if (businessesPromise && typeof businessesPromise.then === 'function') {
      businessesPromise
        .then((result) => {
          log(`getBusinesses() resolved in ${(performance.now() - t0).toFixed(1)}ms`, result);
          if (!result || result.error) {
            logError('getBusinesses() returned an error payload', result);
          }
        })
        .catch((err) => {
          logError(`getBusinesses() rejected after ${(performance.now() - t0).toFixed(1)}ms`, err);
        });
    } else {
      logWarn('getBusinesses() did not return a promise — cannot track resolution');
    }

    const affiliateT0 = performance.now();
    const affiliatePromise = dispatch(affiliateActions.getAffiliateBusinesses());
    log('getAffiliateBusinesses() dispatched — promise', affiliatePromise);

    if (affiliatePromise && typeof affiliatePromise.then === 'function') {
      affiliatePromise
        .then((result) => {
          log(`getAffiliateBusinesses() resolved in ${(performance.now() - affiliateT0).toFixed(1)}ms`, result);
          if (!result || result.error) {
            logError('getAffiliateBusinesses() returned an error payload', result);
          }
        })
        .catch((err) => {
          logError(`getAffiliateBusinesses() rejected after ${(performance.now() - affiliateT0).toFixed(1)}ms`, err);
        });
    } else {
      logWarn('getAffiliateBusinesses() did not return a promise — cannot track resolution');
    }

    // Safety timeout — if nothing has populated after 10 s, log the redux slice
    const timeout = setTimeout(() => {
      logWarn('10 s timeout hit — initial fetches may be hanging. Check network tab.');
    }, 10000);

    return () => clearTimeout(timeout);
  }, [dispatch]);

  // ── Log state changes so we know when (if) they arrive ───────────────────
  useEffect(() => {
    log('rakutenApprovedMids changed', { count: rakutenApprovedMids.length, data: rakutenApprovedMids });
  }, [rakutenApprovedMids]);

  useEffect(() => {
    log('affiliateBusinesses changed', { count: affiliateBusinesses.length, data: affiliateBusinesses });
  }, [affiliateBusinesses]);

  useEffect(() => {
    log('rakutenLoading changed', rakutenLoading);
  }, [rakutenLoading]);

  // ── Map product → coupon ──────────────────────────────────────────────────
  const mapProductToCoupon = (product, index) => {
    const price = parseFloat(product.price);
    const sale = parseFloat(product.sale_price);
    const discountPercent = calculateSavings(price, sale);

    const defaultCategory = selectedBusiness?.categories?.[0] ?? null;
    let type, amount_off, percentage_off;
    if (discountPercent >= 10) {
      type = COUPON_TYPES.PERCENTAGE_OFF;
      percentage_off = discountPercent;
    } else {
      type = COUPON_TYPES.AMOUNT_OFF;
      amount_off = parseFloat((price - sale).toFixed(2));
    }

    const flyerDates = {
      start: parseISOString(product?.created_on),
      end: parseISOString(
        new Date(new Date(product?.created_on).getTime() + 30 * 24 * 60 * 60 * 1000)
      ),
    };

    return {
      localId: `local-${Date.now()}-${index}`,
      title:
        product.product_name.length > 35
          ? product.product_name.slice(0, 32) + '...'
          : product.product_name,
      description: product.short_description,
      type,
      amount_off,
      percentage_off,
      price: sale,
      image_url: product.image_url
        ? [{ image_url: product.image_url, sequence: 1 }]
        : [],
      link_url: product.link_url,
      published_at: flyerDates.start,
      expiration_date: flyerDates.end,
      expiry_type: COUPON_EXPIRY_TYPES.DATE,
      state: COUPON_STATES.PUBLISHED,
      auto_accept: true,
      is_affiliate_or_flyer: true,
      affiliate_link: product.link_url,
      categories: defaultCategory ? [defaultCategory] : [],
    };
  };

  // ── Fetch products ────────────────────────────────────────────────────────
  const handleFetchProducts = () => {
    log('handleFetchProducts called', { inputValue, trimmed: inputValue.trim() });
    setShowCustomCoupon(false);
    setProductsRequested(true);
    setLoading(true);

    if (inputValue.trim()) {
      const t0 = performance.now();
      log('dispatching getProductsByMids', { mids: inputValue.trim() });

      const promise = dispatch(rakutenActions.getProductsByMids({ mids: inputValue.trim() }));

      if (promise && typeof promise.then === 'function') {
        promise
          .then((result) => {
            log(`getProductsByMids() resolved in ${(performance.now() - t0).toFixed(1)}ms`, result);
            if (!result || result.error) {
              logError('getProductsByMids() returned an error payload', result);
            }
          })
          .catch((err) => {
            logError(`getProductsByMids() rejected after ${(performance.now() - t0).toFixed(1)}ms`, err);
            setLoading(false);
          });
      } else {
        logWarn('getProductsByMids() did not return a promise');
      }

      // Safety timeout for product fetch
      setTimeout(() => {
        if (loading) {
          logWarn('15 s timeout — getProductsByMids may be hanging. Check network tab for the request URL and response.');
        }
      }, 15000);
    } else {
      logWarn('handleFetchProducts — inputValue was empty, skipping dispatch');
      setLoading(false);
    }
  };

  // ── React to products arriving ────────────────────────────────────────────
  useEffect(() => {
    log('products state changed', products);

    if (products?.data?.products) {
      log('products.data.products count', products.data.products.length);

      //Temporarily disabling 10%+ off filter to show all products, as per Rakuten's request. Will re-enable in the future.
      const filtered = products.data.products.filter((p) => {
        const price = parseFloat(p.price);
        const sale = parseFloat(p.sale_price);
        return calculateSavings(price, sale) > 10;
      });
      // const filtered = products.data.products;

      log('filtered products (>10% off) count', filtered.length);

      const sorted = filtered.sort((a, b) => {
        return (
          calculateSavings(parseFloat(b.price), parseFloat(b.sale_price)) -
          calculateSavings(parseFloat(a.price), parseFloat(a.sale_price))
        );
      });

      setFilteredProducts(sorted);
      setLoading(false);
      setCurrentPage(1);
    } else {
      if (productsRequested) {
        logWarn('products changed but products.data.products is missing', {
          hasData: !!products?.data,
          keys: products?.data ? Object.keys(products.data) : 'n/a',
        });
      }
    }
  }, [products]);

  // ── Pagination slice ──────────────────────────────────────────────────────
  useEffect(() => {
    const lastPostIndex = currentPage * postsPerPage;
    const firstPostIndex = lastPostIndex - postsPerPage;
    setFirstProductIndex(firstPostIndex + 1);
    setLastProductIndex(lastPostIndex);

    const currentSlice = filteredProducts
      .slice(firstPostIndex, lastPostIndex)
      .map((product, index) => mapProductToCoupon(product, index));

    log('pagination slice', {
      currentPage,
      firstPostIndex,
      lastPostIndex,
      sliceCount: currentSlice.length,
    });

    setDiscountedProducts(currentSlice);
  }, [currentPage, filteredProducts]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleReset = () => {
    // log('handleRemove called', { localId, showCustomCoupon });
    if (showCustomCoupon) {
      resetData();
      setProductsRequested(false);
      setShowCustomCoupon(false);
    }
  };

  const resetData = () => {
    log('resetData called');
    setFilteredProducts([]);
    setDiscountedProducts([]);
  };

  const handleCustomCoupon = () => {
    log('handleCustomCoupon called');
    resetData();
    setShowCustomCoupon(true);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  log('render', {
    selectedBusinessId,
    productsRequested,
    showCustomCoupon,
    loading,
    filteredProductsCount: filteredProducts.length,
    discountedProductsCount: discountedProducts.length,
    rakutenLoading,
    affiliateBusinessesCount: affiliateBusinesses.length,
    rakutenApprovedMidsCount: rakutenApprovedMids.length,
  });

  return (
    <AppWrapper
      showHeader
      showSideNav
      showHeadingRow
      headingText="🎯 Rakuten Coupon & Product Tool"
    >
      <div className="col-md-12">
        <Breadcrumbs locationArray={['Admin', 'Rakuten Import']} />

        <AffiliateBusinessSelector
          affiliateBusinesses={affiliateBusinesses}
          affiliateProgramLoading={rakutenLoading}
          affiliateProgramBusinesses={rakutenApprovedMids}
          inputValue={inputValue}
          affiliateType={AFFILIATE_TYPE}
          onInputValueChange={setInputValue}
          selectedBusiness={selectedBusiness}
          productsRequested={productsRequested}
          onProductsRequestedChange={setProductsRequested}
          onSelectedBusinessIdChange={(id) => {
            log('onSelectedBusinessIdChange', id);
            setSelectedBusinessId(id);
          }}
          onSelectedBusinessChange={(biz) => {
            log('onSelectedBusinessChange', biz);
            setSelectedBusiness(biz);
          }}
          handleFetchProducts={handleFetchProducts}
          OnShowCustomCoupon={handleCustomCoupon}
        />

        {selectedBusinessId && productsRequested && (
          <div>
            {showCustomCoupon ? (
              <div>
                {showCustomCoupon && (
                  <AffiliateCustomCoupon
                    affiliateBusinesses={affiliateBusinesses}
                    selectedBusinessId={selectedBusinessId}
                    dispatch={dispatch}
                    inputValue={inputValue}
                    onClose={handleReset}
                  />
                )}
              </div>
            ) : (
              <div>
                <ProductsStatus
                  title="🛍️ Discounted Products (10%+ off)"
                  items={filteredProducts}
                  firstIndex={firstProductIndex}
                  lastIndex={lastProductIndex}
                  loading={loading}
                  displayedItems={discountedProducts}
                  minDiscount={10}
                  productsRequested={productsRequested}
                />
                <Pagination
                  totalPosts={filteredProducts.length}
                  postsPerPage={postsPerPage}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                />

                <AffiliateCouponsList
                  coupons={discountedProducts || []}
                  loading={loading}
                  affiliateBusinesses={affiliateBusinesses}
                  selectedBusinessId={selectedBusinessId}
                  inputValue={inputValue}
                  dispatch={dispatch}
                  selectedBusiness={selectedBusiness}
                  showDealFields={true}
                  defaultCategory={selectedBusiness?.categories?.[0] || null}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </AppWrapper>
  );
};

export default RakutenCouponList;